import { useEffect, useLayoutEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, GraduationCap, Award, Heart } from "lucide-react";
import axios from "axios";
import { API } from "@/apiConfig";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: "easeOut" },
});

const ICONS = [GraduationCap, Award, GraduationCap, Award, Heart];

const DEFAULTS = {
  bio_title: "Sophrologue caycédienne à Vanves",
  bio_text1: "Bienvenue. Je suis Céline, sophrologue caycédienne certifiée, installée à Vanves dans les Hauts-de-Seine.",
  bio_text2: "Ma pratique est fondée sur une profonde conviction : chaque être humain porte en lui les ressources nécessaires à son épanouissement.",
  bio_text3: "J'accompagne des adultes, des adolescents et des enfants dans leur chemin vers le mieux-être, avec bienveillance, rigueur et enthousiasme.",
  portrait_image: "https://images.unsplash.com/photo-1618919306916-94a2098b21b0?q=85&w=800&auto=format&fit=crop",
  portrait_image_alt: "Eudemonie sophrologue",
  form1_title: "À renseigner dans l'admin",
  form1_org: "—",
  form1_year: "—",
  val1_title: "Bienveillance",
  val1_desc: "Un espace sans jugement où chacun peut explorer son monde intérieur à son rythme.",
  val2_title: "Rigueur",
  val2_desc: "Une pratique fondée sur la méthode caycédienne authentique, avec une formation continue permanente.",
  val3_title: "Personnalisation",
  val3_desc: "Chaque accompagnement est unique, adapté à la personne, à ses besoins et à ses objectifs.",
  formations_kicker: "Parcours",
  formations_title: "Formation & certifications",
  values_title: "Mes valeurs",
  values_cta_label: "Prendre rendez-vous",
};

export default function APropos({ initialPage }) {
  const [c, setC] = useState({});

  useLayoutEffect(() => {
    if (initialPage?.content) setC(initialPage.content);
  }, [initialPage]);

  useEffect(() => {    axios.get(`${API}/public/pages/a-propos`).then(r => { if (r.data?.content) setC(r.data.content); }).catch(() => {});
  }, []);

  const g = key => c[key] || DEFAULTS[key] || "";

  // Build formations list — only show entries that have a title filled in
  const formations = [1, 2, 3, 4, 5]
    .map(n => ({ title: g(`form${n}_title`), org: g(`form${n}_org`), year: g(`form${n}_year`), icon: ICONS[n - 1] }))
    .filter(f => f.title && f.title !== "—" && !f.title.startsWith("À renseigner"));

  const values = [
    { title: g("val1_title"), desc: g("val1_desc") },
    { title: g("val2_title"), desc: g("val2_desc") },
    { title: g("val3_title"), desc: g("val3_desc") },
  ].filter(v => v.title);

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="section-gap bg-bone">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp()}>
              <p className="font-manrope text-xs text-sage tracking-widest uppercase mb-3">À propos</p>
              <h1 className="font-playfair text-5xl md:text-6xl text-charcoal mb-6">{g("bio_title")}</h1>
              <p className="font-manrope text-charcoal-muted leading-relaxed mb-4">{g("bio_text1")}</p>
              <p className="font-manrope text-charcoal-muted leading-relaxed mb-4">{g("bio_text2")}</p>
              <p className="font-manrope text-charcoal-muted leading-relaxed">{g("bio_text3")}</p>
            </motion.div>
            <motion.div {...fadeUp(0.2)}>
              <img src={g("portrait_image")} alt={g("portrait_image_alt")} className="rounded-[2rem] w-full h-[500px] object-cover shadow-xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Formation */}
      {formations.length > 0 && (
        <section className="section-gap bg-bone-light">
          <div className="section-container">
            <motion.div {...fadeUp()} className="text-center mb-12">
              <p className="font-manrope text-xs text-sage tracking-widest uppercase mb-3">{g("formations_kicker")}</p>
              <h2 className="font-playfair text-4xl md:text-5xl text-charcoal">{g("formations_title")}</h2>
            </motion.div>
            <div className="max-w-2xl mx-auto space-y-4">
              {formations.map((item, i) => (
                <motion.div key={i} {...fadeUp(i * 0.1)} data-testid={`formation-${i}`}
                  className="flex items-start gap-5 card-soft py-5 px-6">
                  <div className="w-10 h-10 rounded-xl bg-sage/10 flex items-center justify-center shrink-0">
                    <item.icon size={18} className="text-sage" />
                  </div>
                  <div className="flex-1">
                    <p className="font-manrope font-medium text-charcoal text-sm">{item.title}</p>
                    {item.org && item.org !== "—" && <p className="font-manrope text-xs text-charcoal-muted">{item.org}</p>}
                  </div>
                  {item.year && item.year !== "—" && (
                    <span className="font-manrope text-xs text-charcoal-soft whitespace-nowrap">{item.year}</span>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Valeurs */}
      {values.length > 0 && (
        <section className="section-gap bg-sage">
          <div className="section-container">
            <motion.div {...fadeUp()} className="text-center mb-12">
              <h2 className="font-playfair text-4xl md:text-5xl text-white">{g("values_title")}</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {values.map((v, i) => (
                <motion.div key={i} {...fadeUp(i * 0.1)} data-testid={`valeur-${i}`} className="bg-white/10 rounded-2xl p-8 border border-white/20 text-center">
                  <h3 className="font-playfair text-2xl text-white mb-4">{v.title}</h3>
                  <p className="font-manrope text-sm text-white/75 leading-relaxed">{v.desc}</p>
                </motion.div>
              ))}
            </div>
            <motion.div {...fadeUp(0.4)} className="text-center mt-10">
              <Link to="/prendre-rendez-vous" className="rounded-full bg-white text-sage-dark hover:bg-bone px-8 py-4 font-manrope font-medium text-sm transition-all duration-300 shadow-md inline-flex items-center gap-2">
                {g("values_cta_label")} <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
}
